import {
  quantitative,
  section,
  number,
  choice,
  equation,
  table,
  example,
} from '../batch2/shared.ts';
export const batch3Reason = [
  quantitative(
    'reason-flight-tickets',
    'reason',
    'Flight tickets',
    [
      section('flow', 'Count entries and exits', [
        'A route can be represented as a directed graph: each airport is a vertex, and each ticket is an arrow from its origin to its destination. A usable itinerary follows arrows in their stated direction. The important information for choosing a starting point is how many arrows enter and leave each vertex, not its location on a drawing.',
        'At an airport visited in the middle of a continuous itinerary, every arrival must be paired with a departure. If the route begins and ends at different airports, the starting airport needs one extra departure and the ending airport needs one extra arrival. Every other airport has equal counts. This is a necessary condition for using each ticket exactly once: count the visits and the unpaired edges, rather than guessing a flight order.',
        equation(
          'Endpoint balance',
          'departures − arrivals',
          '+1 at the start; −1 at the end; 0 elsewhere',
          'These differences apply to a route with distinct endpoints.',
        ),
        'Balanced counts alone do not establish that all the tickets belong to one usable route. For example, two disconnected cycles have balanced counts everywhere but cannot be flown as one uninterrupted trip. After identifying possible endpoints, connectivity or an explicit route must still be checked.',
        example(
          'A small ticket bundle',
          'Suppose the tickets are P→Q, Q→R and R→Q. P has one extra departure; Q has one extra arrival; R is balanced. The route P→Q→R→Q uses every ticket, so the necessary endpoint condition is attainable.',
          table(
            'Small bundle balance',
            ['Airport', 'Departures', 'Arrivals'],
            [
              ['P', '1', '0'],
              ['Q', '1', '2'],
              ['R', '1', '1'],
            ],
          ),
          'Starting at Q can consume tickets, but can never reach P to use its outbound ticket. A locally available move is not proof that a complete itinerary remains possible.',
        ),
      ]),
      section(
        'route',
        'Apply the balance and verify a route',
        [
          'For the daily bundle, A has two departures and one arrival; B has two departures and three arrivals. C and D each have two of each. Therefore A must be the start and B the finish. Choosing an airport because it has many tickets would miss the distinction between total activity and the difference between arrivals and departures.',
          table(
            'The daily balance',
            ['Airport', 'Out', 'In', 'Difference'],
            [
              ['A', '2', '1', '+1'],
              ['B', '2', '3', '−1'],
              ['C', '2', '2', '0'],
              ['D', '2', '2', '0'],
            ],
          ),
          'One complete itinerary is A→B→C→A→D→B→D→C→B. It uses all eight directed tickets once. The route supplies the sufficiency check missing from the count alone. Reversing this sequence would not solve the same problem: the tickets specify directed flights, not undirected connections. If an extra B→A ticket were added, all four airports would balance and this itinerary could be closed into a circuit.',
        ],
        'Daily application',
      ),
    ],
    choice(
      'b3-flight',
      'You have exactly these eight one-way tickets: A→B, A→D, B→C, B→D, C→A, C→B, D→B and D→C. You must use every ticket exactly once in one continuous itinerary, with no extra flights. Which airport must you start at?',
      ['A', 'B', 'C', 'D'],
      0,
      'A is the only airport with one more departure than arrival. A→B→C→A→D→B→D→C→B verifies that a complete itinerary exists.',
      ['flow', 'route'],
    ),
  ),
  quantitative(
    'reason-stone-game',
    'reason',
    'Twenty stones',
    [
      section('states', 'Reason backward from a finished game', [
        'A strategy must succeed against every legal reply, not merely against a cooperative opponent. For a finite take-away game, describe a position by the number of stones left and whose turn it is. Here both players have the same permitted moves, so the count alone determines whether the player about to move can force a win.',
        'Call a position winning if at least one legal move leaves a losing position to the other player. Call it losing if every legal move leaves a winning position. These definitions concern optimal play. A winning position can still be lost by choosing badly, and a player in a losing position can win if the opponent makes a mistake.',
        table(
          'Backward rule for the player about to move',
          ['Position', 'What must be established'],
          [
            ['Winning', 'At least one legal move leaves the opponent a losing position.'],
            ['Losing', 'Every legal move leaves the opponent a winning position.'],
          ],
        ),
        'With no stones left, the player about to move has already lost: the previous player took the last stone. This supplies a base case. Compute the status of one stone, then two, and continue upward. Every legal move goes to a smaller count whose status has already been established, so the reasoning does not circle back to an unproved assumption.',
        example(
          'A different move set',
          'If a player may take one or two stones, zero is losing, one and two are winning, and three is losing because either move gives the opponent a winning position. Four and five are then winning by leaving three.',
          table(
            'Take one or two',
            ['Remaining', 'Status', 'Winning move, if any'],
            [
              ['0', 'Losing', '—'],
              ['1', 'Winning', 'Take 1'],
              ['2', 'Winning', 'Take 2'],
              ['3', 'Losing', '—'],
              ['4', 'Winning', 'Take 1'],
              ['5', 'Winning', 'Take 2'],
            ],
          ),
          'The repeating pattern is a result to justify, not a rule that transfers automatically to other allowed moves. Adding a new move changes which positions are safe to leave.',
        ),
      ]),
      section(
        'solve',
        'Use all three permitted moves',
        [
          'For moves of one, three or four stones, backward evaluation gives losing counts 0, 2, 7, 9, 14, 16 and 21 up through 21. From 20, taking four leaves 16. Taking one leaves 19, and taking three leaves 17; both are winning for the opponent.',
          table(
            'Choices from twenty',
            ['Take', 'Leave', 'Opponent’s status'],
            [
              ['1', '19', 'Winning'],
              ['3', '17', 'Winning'],
              ['4', '16', 'Losing'],
            ],
          ),
          'Thus four is the required opening. The later strategy is to keep choosing a move to a losing count whenever the opponent returns a winning count. This is a complete strategic rule, rather than just a promising first move. The method relies on the finite decreasing state and on knowing all legal moves; hidden information, chance moves or a rule that the last taker loses would require a different state analysis.',
        ],
        'Daily application',
      ),
    ],
    number(
      'b3-stones',
      'Two players alternate taking exactly 1, 3 or 4 stones from a pile, never taking more than remain. Taking the last stone wins. You move first with 20 stones, and your opponent plays optimally. How many stones must you take on your first move to guarantee a win?',
      4,
      'stones',
      'Taking four leaves the losing position 16; the other legal openings leave winning positions for the opponent.',
      ['states', 'solve'],
      0,
    ),
  ),
  quantitative(
    'reason-fuel-loop',
    'reason',
    'A circular delivery route',
    [
      section('balance', 'Track a running resource balance', [
        'A total budget says whether a task is possible in aggregate, but a running budget says whether it can be carried out in a particular order. A route can have enough fuel overall and still strand a vehicle before it reaches a later supply. At each station, add the available fuel and then subtract the fuel required for the next leg.',
        'For a chosen starting point, write the net changes in travel order. A prefix sum is the sum of the first few changes. Beginning with an empty tank, every prefix sum must be nonnegative. The final sum also must be nonnegative, but checking only the final sum discards the intermediate constraints.',
        equation(
          'Fuel ledger',
          'fuel after a leg',
          'previous fuel + station supply − leg cost',
          'Use the same fuel units throughout. Fuel is collected before that station’s departure.',
        ),
        example(
          'Enough in total, too late in order',
          'Three stations have net changes −2, +3 and −1. Starting at the first station fails immediately even though the total is zero. Starting at the second gives balances 3, 2 and 0, so the same supplies work in a different cyclic order.',
          table(
            'Start at the second station',
            ['Leg in order', 'Net change', 'Tank afterward'],
            [
              ['Second', '+3', '3'],
              ['Third', '−1', '2'],
              ['First', '−2', '0'],
            ],
          ),
          'No extra fuel was created by changing the start. The change merely moved a reserve-building segment ahead of a segment that consumes the reserve.',
        ),
        'One useful search method is to compute cumulative changes from an arbitrary start, then try the station just after a minimum cumulative balance. Intuitively this places the deepest deficit behind the new start. When the total is zero, every new prefix is the old cumulative level minus that minimum, wrapping around if necessary, so none is negative. There can be several valid starts; a question asking for one unique number must specify a tie rule.',
      ]),
      section(
        'daily',
        'Check the requested starting station',
        [
          'The six daily net changes are +1, −3, +3, −3, +1 and +1. Starting at station 1 gives balances 1 and then −2, so it fails. Starting at station 2 fails on its first leg. Starting at station 3 gives the balances in the table.',
          table(
            'Start at station three',
            ['Depart station', 'Fuel after reaching next'],
            [
              ['3', '3'],
              ['4', '0'],
              ['5', '1'],
              ['6', '2'],
              ['1', '3'],
              ['2', '0'],
            ],
          ),
          'Every balance is nonnegative, including exact exhaustion on arrival. Station 3 is therefore feasible, and the failures of stations 1 and 2 prove it is the lowest-numbered feasible start. Station 5 also works; ignoring the tie rule would make a supposedly unique starting-station question ambiguous. The unlimited tank matters: a capacity limit could force excess fuel to be discarded and invalidate the simple cumulative-sum criterion.',
        ],
        'Daily application',
      ),
    ],
    number(
      'b3-fuel',
      'Six stations lie on a one-way loop 1→2→3→4→5→6→1. Their fuel supplies are respectively 4, 1, 5, 2, 3, 5 liters; the legs leaving them require respectively 3, 4, 2, 5, 2, 4 liters. Start at any station with an empty unlimited-capacity tank, collect each station’s supply once before leaving, and complete one lap. Arriving with zero fuel is allowed. What is the lowest-numbered starting station that works?',
      3,
      'station number',
      'Stations 1 and 2 fail; from station 3 the running balances are 3, 0, 1, 2, 3, 0.',
      ['balance', 'daily'],
      0,
    ),
  ),
  quantitative(
    'reason-grid-route',
    'reason',
    'A blocked junction',
    [
      section('count', 'Encode paths as sequences', [
        'On a rectangular street grid, a route that only travels east or north never backtracks. It can be recorded as a sequence of E and N symbols. If a destination requires a fixed number of each move, every ordering of those symbols reaches it. This turns a drawing problem into a counting problem without losing the individual routes.',
        'To count sequences with e east moves and n north moves, choose which n of the e + n positions hold N. The remaining positions automatically hold E. The count is C(e + n,n), a combination. To see why order does not count twice, first choose the n positions in order, then divide by n!, the number of orders of the same selected positions.',
        equation(
          'Choose positions',
          'C(m,k)',
          'm(m − 1)…(m − k + 1) / k!',
          'Here k! is k × (k − 1) × … × 1; it removes repeated orders of each selection.',
        ),
        example(
          'A smaller grid',
          'For two east moves and two north moves there are C(4,2) = 6 routes. To pass through (1,1), a route must first choose EN or NE, then choose EN or NE again. The two choices before and the two after combine to make four routes through the junction.',
          table(
            'Route decomposition',
            ['Part', 'Choices'],
            [
              ['Origin to (1,1)', 'EN or NE: 2'],
              ['(1,1) to (2,2)', 'EN or NE: 2'],
              ['Complete routes through (1,1)', '2 × 2 = 4'],
              ['Routes avoiding (1,1)', '6 − 4 = 2'],
            ],
          ),
          'Multiplication works because every before-route can be paired with every after-route, and each complete path has exactly one such decomposition.',
        ),
        'Subtracting the forbidden routes is often easier than describing all the allowed detours. With a single forbidden junction, each affected monotone route is counted exactly once. Multiple forbidden junctions need more care because a path can visit several and be subtracted repeatedly.',
      ]),
      section(
        'apply',
        'Count routes through the closed junction',
        [
          'From (0,0) to (4,3), every route has seven moves, three of them north. There are C(7,3) = 35 routes in total. Reaching the closed junction (2,1) takes two east moves and one north move, giving three orders. From there to (4,3) takes two east and two north, giving six orders.',
          equation(
            'Avoid the junction',
            'allowed routes',
            '35 − (3 × 6) = 17',
            'Subtract exactly the routes with a before/after split at the forbidden junction.',
          ),
          'This count treats a route as its complete sequence of street segments, not merely its final destination. It assumes the only obstruction is the junction and that all other grid edges are available. Counting 35 minus one would confuse a forbidden vertex with a single route: many different paths can pass through the same vertex.',
        ],
        'Daily application',
      ),
    ],
    number(
      'b3-grid',
      'A street grid has coordinates (0,0) through (4,3). Starting at (0,0), you may move one block east or one block north at a time. Every street is available, but you may not visit the closed junction (2,1). How many distinct routes reach (4,3)?',
      17,
      'routes',
      'There are 35 monotone routes, of which 3 × 6 = 18 pass through the closed junction.',
      ['count', 'apply'],
      0,
    ),
  ),
  quantitative(
    'reason-depot',
    'reason',
    'A depot on one street',
    [
      section('distance', 'Match the objective to the right center', [
        'A center depends on what is being minimized. The arithmetic mean balances signed deviations and minimizes a sum of squared distances. A depot minimizing total ordinary travel distance solves a different problem. On a straight street, the distance from coordinate x to coordinate a is |x − a|, the nonnegative gap between them.',
        'Imagine moving the depot a small distance to the right without crossing a destination. Each destination to its right becomes that much closer; each destination to its left becomes that much farther away. The change in the total therefore depends on the count on each side. Moving toward the side with more destinations reduces the total until the counts balance.',
        equation(
          'Change over a short rightward move',
          'change in total distance',
          '(number to left − number to right) × move length',
          'This expression applies between destination coordinates; at a destination, reconsider the side counts.',
        ),
        example(
          'Three delivery points',
          'Take locations 0, 2 and 10. Placing the depot at 2 gives total distance 2 + 0 + 8 = 10. Placing it at the mean, 4, gives 4 + 2 + 6 = 12. The faraway destination pulls the mean rightward, but moving right helps only one destination while hurting two.',
          table(
            'Compare two objectives',
            ['Depot', 'Total ordinary distance', 'Sum of squared distances'],
            [
              ['2', '10', '68'],
              ['4', '12', '56'],
            ],
          ),
          'The mean is not a wrong kind of average; it is answering a different optimization question. State the cost being minimized before selecting a center.',
        ),
        'With an odd number of equally weighted destinations, the middle sorted destination is the unique minimizing coordinate. With an even number, every coordinate between the two middle destinations ties. These statements assume each trip has the same weight. If some destinations require more visits, their counts must be weighted and the unweighted median need not be optimal.',
      ]),
      section(
        'depot',
        'Pair destinations to prove the minimum',
        [
          'The seven daily locations are already sorted. Their middle coordinate is 7. Pairing the outer destinations provides an independent lower bound: travel to 0 and 18 totals at least 18, travel to 1 and 13 totals at least 12, and travel to 4 and 9 totals at least 5. The remaining destination at 7 contributes a nonnegative amount.',
          table(
            'A bound from pairs',
            ['Pair', 'Minimum combined distance'],
            [
              ['0 and 18', '18'],
              ['1 and 13', '12'],
              ['4 and 9', '5'],
              ['Unpaired point 7', '0'],
            ],
          ),
          'The total cannot be below 35. A depot at 7 attains each pair bound simultaneously and gives zero distance to the middle point, so the bound is tight. The question asks for total one-way distance, not the depot coordinate and not round-trip distance. Multiplying by two would answer a different travel accounting convention.',
        ],
        'Daily application',
      ),
    ],
    number(
      'b3-depot',
      'Seven destinations lie at kilometer coordinates 0, 1, 4, 7, 9, 13 and 18 on one straight street. You may locate a depot at any real coordinate on the street. One separate one-way trip goes from the depot to each destination. What is the minimum possible sum of the seven trip distances?',
      35,
      'kilometers',
      'The three outer pairs impose a 35 km lower bound, attained by placing the depot at coordinate 7.',
      ['distance', 'depot'],
      0,
    ),
  ),
];
